import React, { useEffect, useState, Children, Component } from 'react';
import { motion } from 'framer-motion';
import { Server, Globe, Shield, HeadphonesIcon, Cpu, Cloud, BoxIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
// Map string names to actual Lucide components for dynamic rendering
const iconMap: Record<string, BoxIcon> = {
  Globe,
  Server,
  Cpu,
  Cloud,
  Shield,
  HeadphonesIcon
};
interface Feature {
  title: string;
  description: string;
  iconName: string;
}
const defaultFeatures: Feature[] = [{
  title: 'Web Hosting',
  description: 'Fast, secure, and reliable shared hosting for your personal or small business websites.',
  iconName: 'Globe'
}, {
  title: 'VPS Servers',
  description: 'Dedicated resources and full root access for applications that need more power.',
  iconName: 'Server'
}, {
  title: 'Dedicated Servers',
  description: 'Bare metal performance for enterprise applications and high-traffic websites.',
  iconName: 'Cpu'
}, {
  title: 'Domain Registration',
  description: 'Find and register the perfect domain name for your brand with free privacy protection.',
  iconName: 'Cloud'
}, {
  title: 'SSL Certificates',
  description: 'Secure your website and build customer trust with our premium SSL certificates.',
  iconName: 'Shield'
}, {
  title: '24/7 Support',
  description: 'Our expert team is always awake and ready to help you with any technical issues.',
  iconName: 'HeadphonesIcon'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
export function Features() {
  const [features, setFeatures] = useState<Feature[]>(defaultFeatures);
  const [headerInfo, setHeaderInfo] = useState({
    subtitle: 'Our Services',
    title: 'Everything you need to succeed online',
    description: 'We provide a comprehensive suite of tools and services designed to make your online journey fast, reliable, and hassle-free.'
  });
  useEffect(() => {
    async function loadFeatures() {
      try {
        const {
          data,
          error
        } = await supabase.from('site_settings').select('key, value').in('key', ['features_content', 'features_header']);
        if (error) throw error;
        if (data) {
          const contentRow = data.find(r => r.key === 'features_content');
          if (contentRow && contentRow.value) {
            setFeatures(contentRow.value as Feature[]);
          }
          const headerRow = data.find(r => r.key === 'features_header');
          if (headerRow && headerRow.value) {
            setHeaderInfo(headerRow.value as typeof headerInfo);
          }
        }
      } catch (err) {
        console.error('Failed to load features:', err);
      }
    }
    loadFeatures();
  }, []);
  return <section id="features" className="py-24 bg-slate-950 relative" data-id="element-1667">
      <div className="container mx-auto px-4 md:px-6" data-id="element-1668">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1669">
          <h2 className="text-fox-500 font-semibold tracking-wide uppercase text-sm mb-3" data-id="element-1670">
            {headerInfo.subtitle}
          </h2>
          <h3 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6" data-id="element-1671">
            {headerInfo.title}
          </h3>
          <p className="text-slate-400 text-lg" data-id="element-1672">{headerInfo.description}</p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-1673">
          {features.map((feature, index) => {
          const IconComponent = iconMap[feature.iconName] || Globe;
          return <motion.div key={index} variants={itemVariants} whileHover={{
            y: -5
          }} className="glass-panel p-8 rounded-2xl group hover:border-fox-500/50 transition-colors duration-300 relative overflow-hidden" data-id="element-1674">
                <div className="absolute top-0 right-0 w-32 h-32 bg-fox-500/5 rounded-full blur-3xl -mr-10 -mt-10 transition-opacity group-hover:bg-fox-500/10" data-id="element-1675" />

                <div className="w-14 h-14 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center mb-6 group-hover:border-fox-500/50 transition-colors relative z-10" data-id="element-1676">
                  <IconComponent className="text-fox-500" size={28} data-id="element-1677" />
                </div>

                <h4 className="text-xl font-bold text-white mb-3 relative z-10" data-id="element-1678">
                  {feature.title}
                </h4>
                <p className="text-slate-400 leading-relaxed relative z-10" data-id="element-1679">
                  {feature.description}
                </p>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}